/**
 * „Inaczej niż zwykle"'s writes: a date's exception (`PUT`/`DELETE /trainer-availability/override`)
 * and a block (`POST`/`DELETE /trainer-availability/block`), each with its dialog or its question.
 *
 * The dialogs hold their drafts here (`model/dateException`, `model/blockDraft`), so the dialog
 * components only draw and forward. A save closes its dialog and says so; a refusal keeps it open
 * and says why in it, act first (`model/scheduleRefusal`). „Przywróć wzorzec" and „Usuń" ask first
 * — mobile's own questions — because neither is undone by a second press. Every write reads the
 * whole booking screen again: the calendar's off-hours tint, its day notes and the free starts all
 * come from these rows.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useDeleteTrainerAvailabilityBlockId,
  useDeleteTrainerAvailabilityOverrideDate,
  usePostTrainerAvailabilityBlock,
  usePutTrainerAvailabilityOverrideDate,
} from '~/shared/api/generated/endpoints/trainer-availability/trainer-availability';
import { ApiError } from '~/shared/api/mutator';
import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';
import { readBookingAgain } from '~/features/credit-ledger';

import { deviationRemoved } from '../api/bookingQueries';
import { blockDraftFrom, blockRequest, type BlockDraft } from '../model/blockDraft';
import {
  exceptionDraftFor,
  exceptionMovedTo,
  exceptionRequest,
  usualHours,
  type ExceptionDraft,
} from '../model/dateException';
import type { Deviation } from '../model/scheduleDeviations';
import { scheduleRefusal, type ScheduleAct } from '../model/scheduleRefusal';
import { listDay } from '../model/scheduleWords';
import type { CivilDay } from '../model/trainerClock';
import type { WorkingWindow } from '../model/workingHours';
import type { ScheduleRead } from './useScheduleRead';

export interface DeviationWrites {
  readonly exception: ExceptionDraft | null;
  /** The weekday's own hours on the dialog's date — „Zwykle: …". */
  readonly exceptionUsual: readonly WorkingWindow[];
  /** Open the dialog on a date — or move the open one there — holding what the date says now. */
  readonly openException: (date: CivilDay) => void;
  readonly editException: (draft: ExceptionDraft) => void;
  readonly saveException: () => void;
  readonly block: BlockDraft | null;
  readonly openBlock: () => void;
  readonly editBlock: (draft: BlockDraft) => void;
  readonly saveBlock: () => void;
  readonly closeDialog: () => void;
  readonly saving: boolean;
  /** Why the open dialog's last save was refused. */
  readonly dialogRefusal: string | null;
  /** Why the last restore or removal was refused — said over the list. */
  readonly listRefusal: string | null;
  readonly ask: (deviation: Deviation) => void;
  /** The line whose undo is being asked about, while the question stands. */
  readonly asking: Deviation | null;
  readonly prompt: ConfirmPrompt | null;
  readonly confirm: () => void;
  readonly dismiss: () => void;
}

export function useDeviationWrites(
  read: ScheduleRead,
  announce: (message: string) => void,
): DeviationWrites {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [exception, setException] = useState<ExceptionDraft | null>(null);
  const [block, setBlock] = useState<BlockDraft | null>(null);
  const [asking, setAsking] = useState<Deviation | null>(null);
  const [dialogRefusal, setDialogRefusal] = useState<string | null>(null);
  const [listRefusal, setListRefusal] = useState<string | null>(null);

  const refusalOf = (act: ScheduleAct, error: unknown) => {
    const refused = scheduleRefusal(
      act,
      error instanceof ApiError ? { status: error.status, code: error.code } : null,
    );
    return `${t(refused.actKey)} ${t(refused.reasonKey, refused.params)}`;
  };
  const landed = (message: string) => {
    announce(message);
    void readBookingAgain(queryClient);
  };

  const putOverride = usePutTrainerAvailabilityOverrideDate({
    mutation: {
      onSuccess: () => {
        setException(null);
        landed(t('web:booking.schedule.exceptionSaved'));
      },
      onError: (error) => {
        setDialogRefusal(refusalOf('saveException', error));
      },
    },
  });
  const postBlock = usePostTrainerAvailabilityBlock({
    mutation: {
      onSuccess: () => {
        setBlock(null);
        landed(t('web:booking.schedule.blockSaved'));
      },
      onError: (error) => {
        setDialogRefusal(refusalOf('saveBlock', error));
      },
    },
  });
  const deleteOverride = useDeleteTrainerAvailabilityOverrideDate({
    mutation: {
      onSuccess: (_answer, { date }) => {
        deviationRemoved(queryClient, { date });
        setAsking(null);
        landed(t('web:booking.schedule.restored'));
      },
      onError: (error) => {
        setAsking(null);
        setListRefusal(refusalOf('removeException', error));
      },
    },
  });
  const deleteBlock = useDeleteTrainerAvailabilityBlockId({
    mutation: {
      onSuccess: (_answer, { id }) => {
        deviationRemoved(queryClient, { blockId: id });
        setAsking(null);
        landed(t('web:booking.schedule.removed'));
      },
      onError: (error) => {
        setAsking(null);
        setListRefusal(refusalOf('removeBlock', error));
      },
    },
  });
  const saving = putOverride.isPending || postBlock.isPending;
  const removing = deleteOverride.isPending || deleteBlock.isPending;

  const promptOf = (deviation: Deviation): ConfirmPrompt => {
    const exceptionAsked = deviation.kind === 'exception';
    const day = listDay(exceptionAsked ? deviation.date : deviation.fromDay, t);
    return {
      title: t(
        exceptionAsked
          ? 'web:booking.trainer.schedule.deviation.removeExceptionTitle'
          : 'web:booking.trainer.schedule.deviation.removeBlockTitle',
        { date: day },
      ),
      description: t(
        exceptionAsked
          ? 'web:booking.trainer.schedule.deviation.removeExceptionBody'
          : 'web:booking.trainer.schedule.deviation.removeBlockBody',
      ),
      confirmLabel: exceptionAsked
        ? t('web:booking.schedule.restore')
        : t('web:booking.trainer.schedule.deviation.removeConfirm'),
      cancelLabel: t('web:booking.schedule.cancel'),
      destructiveConfirm: !exceptionAsked,
      busy: removing,
    };
  };

  return {
    exception,
    exceptionUsual:
      exception === null || read.availability === undefined
        ? []
        : usualHours(read.availability, exception.date),
    openException: (date) => {
      const availability = read.availability;
      if (availability === undefined) return;
      setDialogRefusal(null);
      setException((open) =>
        open === null
          ? exceptionDraftFor(availability, date)
          : exceptionMovedTo(open, availability, date),
      );
    },
    editException: (draft) => {
      setDialogRefusal(null);
      setException(draft);
    },
    saveException: () => {
      if (exception === null || saving) return;
      putOverride.mutate({ date: exception.date, data: exceptionRequest(exception) });
    },
    block,
    openBlock: () => {
      setDialogRefusal(null);
      setBlock(blockDraftFrom(read.nowMs, read.zone));
    },
    editBlock: (draft) => {
      setDialogRefusal(null);
      setBlock(draft);
    },
    saveBlock: () => {
      if (block === null || saving) return;
      postBlock.mutate({ data: blockRequest(block, read.zone) });
    },
    closeDialog: () => {
      if (saving) return;
      setException(null);
      setBlock(null);
      setDialogRefusal(null);
    },
    saving,
    dialogRefusal,
    listRefusal,
    ask: (deviation) => {
      setListRefusal(null);
      setAsking(deviation);
    },
    asking,
    prompt: asking === null ? null : promptOf(asking),
    confirm: () => {
      if (asking === null || removing) return;
      if (asking.kind === 'exception') deleteOverride.mutate({ date: asking.date });
      else deleteBlock.mutate({ id: asking.block.id });
    },
    dismiss: () => {
      if (removing) return;
      setAsking(null);
    },
  };
}
